import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DataManager, ODataV4Adaptor, Predicate, Query } from '@syncfusion/ej2-data';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

type RelatedCondition = { relatedTypeId: number; recordId: number };
type TimelineType = 'All' | 'Comment' | 'Activity' | 'File';

type TimelineItemModel = {
  id: string;
  type: Exclude<TimelineType, 'All'>;
  title: string;
  body: string;
  author: string;
  createdOn: Date;
  isExpanded: boolean;
  isLong: boolean;
  displayDate: string;
  relativeDate: string;
  icon: string;
  typeClass: string;
  fileId?: number;
  fileName?: string;
  canPreview?: boolean;
};

@Component({
  selector: 'app-utility-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnInit, OnChanges {
  @Input() relatedConditions: RelatedCondition[] = [];

  private baseUrl: string = environment.baseUrl.replace('/api/', '/odata/');
  public timelineItems: TimelineItemModel[] = [];
  public activeFilter: TimelineType = 'All';
  public isLoading = false;
  public previewImageUrl: SafeUrl | null = null;
  public previewTitle = 'File Preview';
  public previewKind: 'pdf' | 'image' | null = null;
  public previewPdfVisible = false;
  public previewPdfBytes: Uint8Array | null = null;
  public previewImageVisible = false;
  public readonly filters: TimelineType[] = ['All', 'Comment', 'Activity', 'File'];
  private readonly previewLength = 140;
  private loadSequence = 0;
  private previewObjectUrl: string | null = null;

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadTimeline();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['relatedConditions'] && !changes['relatedConditions'].firstChange) {
      this.loadTimeline();
    }
  }

  get filteredTimelineItems(): TimelineItemModel[] {
    if (this.activeFilter === 'All') {
      return this.timelineItems;
    }
    return this.timelineItems.filter((x) => x.type === this.activeFilter);
  }

  get hasData(): boolean {
    return this.timelineItems.length > 0;
  }

  get totalCount(): number {
    return this.timelineItems.length;
  }

  get filterSummary(): { key: TimelineType; label: string; count: number }[] {
    return this.filters.map((f) => {
      const count = f === 'All' ? this.totalCount : this.timelineItems.filter((x) => x.type === f).length;
      return { key: f, label: f === 'All' ? 'All Updates' : `${f}s`, count };
    });
  }

  selectFilter(type: TimelineType): void {
    this.activeFilter = type;
  }

  toggleExpand(item: TimelineItemModel): void {
    item.isExpanded = !item.isExpanded;
  }

  previewFile(item: TimelineItemModel): void {
    if (!item.fileId || !item.canPreview) {
      return;
    }

    this.clearPreviewUrl();
    this.http.get(environment.baseUrl + `FileAttachment/download/${item.fileId}`, { responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          this.previewKind = this.getPreviewType(item.fileName || '');
          this.previewTitle = item.fileName || item.title;
          if (this.previewKind === 'pdf') {
            blob.arrayBuffer().then((buffer) => {
              this.previewPdfBytes = new Uint8Array(buffer);
              this.previewPdfVisible = true;
            });
            this.previewImageUrl = null;
          } else if (this.previewKind === 'image') {
            this.previewObjectUrl = URL.createObjectURL(blob);
            this.previewImageUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewObjectUrl);
            this.previewImageVisible = true;
          }
        },
      });
  }

  closePreview(): void {
    this.clearPreviewUrl();
    this.previewImageVisible = false;
    this.previewKind = null;
    this.previewTitle = 'File Preview';
  }

  onPdfPreviewClosed(): void {
    this.previewPdfBytes = null;
    this.previewPdfVisible = false;
    this.previewKind = null;
    this.previewTitle = 'File Preview';
  }

  trackById(index: number, item: TimelineItemModel): string {
    return item.id;
  }

  getBodyPreview(item: TimelineItemModel): string {
    if (!item.body) {
      return '';
    }
    if (item.isExpanded || item.body.length <= this.previewLength) {
      return item.body;
    }
    return `${item.body.slice(0, this.previewLength)}...`;
  }

  private async loadTimeline(): Promise<void> {
    const sequence = ++this.loadSequence;
    this.isLoading = true;
    if (!this.relatedConditions?.length) {
      this.timelineItems = [];
      this.isLoading = false;
      return;
    }

    const token = this.authService.currentUserValue?.token;
    const headers = [{ Authorization: 'Bearer ' + token }];
    const predicate = this.buildRelatedPredicate(this.relatedConditions);
    if (!predicate) {
      this.timelineItems = [];
      this.isLoading = false;
      return;
    }

    const activityDm = new DataManager({
      url: this.baseUrl + 'Activity',
      adaptor: new ODataV4Adaptor(),
      headers,
    });
    const commentsDm = new DataManager({
      url: this.baseUrl + 'Comments',
      adaptor: new ODataV4Adaptor(),
      headers,
    });
    const filesDm = new DataManager({
      url: this.baseUrl + 'FileAttachment',
      adaptor: new ODataV4Adaptor(),
      headers,
    });

    try {
      const [activityResult, commentsResult, filesResult] = await Promise.all([
        activityDm.executeQuery(
          new Query()
            .expand('ActivityType($select=Name),CreatedBy($select=FirstName,LastName)')
            .where(predicate)
            .take(100)
        ),
        commentsDm.executeQuery(
          new Query()
            .expand('CreatedBy($select=FirstName,LastName)')
            .where(predicate)
            .take(100)
        ),
        filesDm.executeQuery(
          new Query()
            .expand('CreatedBy($select=FirstName,LastName)')
            .where(predicate)
            .take(100)
        ),
      ]);

      if (sequence !== this.loadSequence) {
        return;
      }

      const activityItems = (activityResult as any)?.result?.map((a: any) => {
        const createdOn = new Date(a.DateTime || a.CreatedOn || new Date());
        const author = this.fullName(a.CreatedBy?.FirstName, a.CreatedBy?.LastName);
        const body = a.Name || '-';
        return {
          id: `A_${a.Id}`,
          type: 'Activity',
          title: a.ActivityType?.Name ? `${a.ActivityType.Name}` : 'Activity',
          body,
          author,
          createdOn,
          isExpanded: false,
          isLong: body.length > this.previewLength,
          displayDate: this.formatDate(createdOn),
          relativeDate: this.relativeDate(createdOn),
          icon: 'e-icons e-clock',
          typeClass: 'timeline-activity',
        } as TimelineItemModel;
      }) ?? [];

      const commentItems = (commentsResult as any)?.result?.map((c: any) => {
        const createdOn = new Date(c.CreatedOn || new Date());
        const author = this.fullName(c.CreatedBy?.FirstName, c.CreatedBy?.LastName);
        const body = c.Comment || '-';
        return {
          id: `C_${c.Id}`,
          type: 'Comment',
          title: 'Comment',
          body,
          author,
          createdOn,
          isExpanded: false,
          isLong: body.length > this.previewLength,
          displayDate: this.formatDate(createdOn),
          relativeDate: this.relativeDate(createdOn),
          icon: 'e-icons e-comment-show',
          typeClass: 'timeline-comment',
        } as TimelineItemModel;
      }) ?? [];

      const fileItems = (filesResult as any)?.result?.map((f: any) => {
        const createdOn = new Date(f.CreatedOn || new Date());
        const author = this.fullName(f.CreatedBy?.FirstName, f.CreatedBy?.LastName);
        const fileName = f.FileName || f.Name || '-';
        const size = this.formatFileSize(f.Size);
        const body = size ? `${fileName} (${size})` : fileName;
        return {
          id: `F_${f.Id}`,
          type: 'File',
          title: 'File Uploaded',
          body,
          author,
          createdOn,
          isExpanded: false,
          isLong: body.length > this.previewLength,
          displayDate: this.formatDate(createdOn),
          relativeDate: this.relativeDate(createdOn),
          icon: 'e-icons e-folder',
          typeClass: 'timeline-file',
          fileId: f.Id,
          fileName,
          canPreview: this.getPreviewType(fileName) !== null,
        } as TimelineItemModel;
      }) ?? [];

      this.timelineItems = [...activityItems, ...commentItems, ...fileItems]
        .sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
    } catch {
      this.timelineItems = [];
    } finally {
      if (sequence === this.loadSequence) {
        this.isLoading = false;
      }
    }
  }

  private buildRelatedPredicate(conditions: RelatedCondition[]): Predicate | null {
    let finalPredicate: Predicate | null = null;
    for (const condition of conditions) {
      const p = new Predicate('RelatedTypeId', 'equal', condition.relatedTypeId).and(
        'RelatedId',
        'equal',
        condition.recordId
      );
      finalPredicate = finalPredicate ? finalPredicate.or(p) : p;
    }
    return finalPredicate;
  }

  private formatDate(value: string | Date): string {
    if (!value) {
      return '';
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  private relativeDate(value: Date): string {
    const d = new Date(value);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((today.getTime() - dateOnly.getTime()) / 86400000);
    const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    if (diffDays === 0) {
      return `Today, ${timePart}`;
    }
    if (diffDays === 1) {
      return `Yesterday, ${timePart}`;
    }
    if (diffDays > 1 && diffDays <= 7) {
      return `${diffDays} days ago`;
    }
    return this.formatDate(d);
  }

  private formatFileSize(rawSize: unknown): string {
    const size = Number(rawSize);
    if (!Number.isFinite(size) || size <= 0) {
      return '';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let current = size;
    let idx = 0;
    while (current >= 1024 && idx < units.length - 1) {
      current /= 1024;
      idx += 1;
    }
    return `${current.toFixed(current >= 100 || idx === 0 ? 0 : 1)} ${units[idx]}`;
  }

  private fullName(first?: string, last?: string): string {
    return `${first ?? ''} ${last ?? ''}`.trim() || 'Unknown';
  }

  private getPreviewType(fileName: string): 'pdf' | 'image' | null {
    const normalized = (fileName || '').toLowerCase();
    if (normalized.endsWith('.pdf')) {
      return 'pdf';
    }
    if (/\.(png|jpg|jpeg|gif|bmp|webp|svg)$/.test(normalized)) {
      return 'image';
    }
    return null;
  }

  private clearPreviewUrl(): void {
    this.previewImageUrl = null;
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }
}
